import classNames from 'classnames'
import {
  type LiHTMLAttributes,
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTheme } from '@opallabs/hooks'
import { Checkbox } from '../Checkbox'
import * as styles from './styles'

type ContextValue = {
  checkboxLabelId: string
  disabled?: boolean
  handleSelection: () => void
  optionText: string
  selected?: boolean
}

const MenuOptionContext = createContext<ContextValue>({
  checkboxLabelId: '',
  disabled: false,
  selected: false,
  handleSelection: () => null,
  optionText: '',
})

type RootProps = Omit<ContextValue, 'checkboxLabelId' | 'handleSelection'> & {
  children: ReactNode
  id: string
  onSelect?: (selected: { id: string; optionText: string }) => void
  optionText: string
}

const Root: React.FC<RootProps> = ({
  children,
  disabled = false,
  id,
  onSelect,
  optionText,
  selected = false,
}) => {
  const [checkboxLabelId] = useState(uuidv4())
  const handleSelection = () => {
    if (disabled) return
    onSelect?.({ id, optionText })
  }

  return (
    <MenuOptionContext.Provider
      value={{
        checkboxLabelId,
        disabled,
        handleSelection,
        optionText,
        selected,
      }}
    >
      {children}
    </MenuOptionContext.Provider>
  )
}
Root.displayName = 'Lapidary: Root ← MenuOption'

const useMenuOptionContext = () => useContext(MenuOptionContext)

type MenuOptionProps = Readonly<
  Omit<LiHTMLAttributes<HTMLLIElement>, 'id'> & {
    children?: ReactNode
    highlightedByKeyboard?: boolean
    setIsFocusableByKeypress?: React.Dispatch<React.SetStateAction<boolean>>
  }
>

const Body = forwardRef<HTMLLIElement, MenuOptionProps>(
  (
    {
      children,
      className,
      highlightedByKeyboard = false,
      setIsFocusableByKeypress,
      ...listItemProps
    },
    ref
  ) => {
    const theme = useTheme()
    const { disabled, handleSelection, selected } = useMenuOptionContext()

    const onKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
      if (e.key === 'Enter') {
        handleSelection()
      }
    }

    return (
      <li
        className={classNames(styles.rootSelector, className)}
        ref={ref}
        role="option"
        aria-selected={selected}
        aria-disabled={disabled}
        css={[
          styles.menuOption({
            selected,
            theme,
            disabled,
            highlightedByKeyboard,
          }),
        ]}
        // used with useListKeyboardNavigation when highlightOnFocus is true to prevent
        // unnecessarily highlighting first item in the list when a different item is clicked
        onMouseDown={() =>
          setIsFocusableByKeypress && setIsFocusableByKeypress(false)
        }
        onFocus={(e) => e.stopPropagation()}
        onClick={handleSelection}
        onKeyDown={onKeyDown}
        {...listItemProps}
      >
        {children}
      </li>
    )
  }
)
Body.displayName = 'Lapidary: Body ← MenuOption'

const OptionText: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className,
  children,
}) => {
  const { checkboxLabelId, optionText } = useMenuOptionContext()

  return (
    <span
      className={classNames([styles.selectors.menuOptionText, className])}
      css={styles.menuText}
      id={checkboxLabelId}
    >
      {optionText}
      {children}
    </span>
  )
}
OptionText.displayName = 'Lapidary: OptionText ← MenuOption'

const CheckboxToggle = () => {
  const theme = useTheme()
  const { checkboxLabelId, selected, disabled } = useMenuOptionContext()
  return (
    <Checkbox
      checked={Boolean(selected)}
      disabled={disabled}
      labedlledbyId={checkboxLabelId}
      onChange={() => null} // list item onClick event will fire instead
      css={styles.checkbox}
      tabIndex={-1}
      theme={theme}
    />
  )
}
CheckboxToggle.displayName = 'Lapidary: CheckboxToggle ← MenuOption'

export const MenuOption = {
  Root,
  Body,
  Checkbox: CheckboxToggle,
  OptionText,
}
